import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createPuc,
  deletePuc,
  downloadPucTemplate,
  listPuc,
  updatePuc,
  type CreatePucRequest,
} from "@/api/empresas";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { showToast } from "@/components/ui/Toast";
import { PucActionsBar } from "./puc/PucActionsBar";
import { PucFormModal } from "./puc/PucFormModal";
import { PucTable } from "./puc/PucTable";

type PucTipo = "" | "_ventas" | "_exterior" | "_legalizacion";

interface PucTabProps {
  empresaId: number;
  tipo: PucTipo;
  title: string;
}

const tipoLabel: Record<PucTipo, string> = {
  "": "Compras",
  _ventas: "Ventas",
  _exterior: "Exterior",
  _legalizacion: "Legalización",
};

export const PucTab = ({ empresaId, tipo, title }: PucTabProps) => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<
    Parameters<typeof PucTable>[0]["cuentas"][number] | null
  >(null);
  const [creating, setCreating] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<
    Parameters<typeof PucTable>[0]["cuentas"][number] | null
  >(null);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["puc", empresaId, tipo] });

  const { data: cuentas = [], isLoading } = useQuery({
    queryKey: ["puc", empresaId, tipo],
    queryFn: () => listPuc(empresaId, tipo),
    enabled: Number.isFinite(empresaId),
  });

  const saveMutation = useMutation({
    mutationFn: (body: CreatePucRequest) =>
      editing
        ? updatePuc(empresaId, editing.id, body, tipo)
        : createPuc(empresaId, body, tipo),
    onSuccess: () => {
      invalidate();
      setEditing(null);
      setCreating(false);
      showToast("success", editing ? "Cuenta actualizada" : "Cuenta creada");
    },
    onError: (err: Error) =>
      showToast("error", "No se pudo guardar", err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deletePuc(empresaId, id, tipo),
    onSuccess: () => {
      invalidate();
      setPendingDelete(null);
      showToast("success", "Cuenta eliminada");
    },
    onError: (err: Error) =>
      showToast("error", "No se pudo eliminar", err.message),
  });

  const onDownloadTemplate = async () => {
    try {
      await downloadPucTemplate(tipo, `plantilla_puc_${tipoLabel[tipo]}.xlsx`);
      showToast("success", "Plantilla descargada");
    } catch (err) {
      showToast("error", "Error al descargar", (err as Error).message);
    }
  };

  return (
    <div className="space-y-3">
      <PucActionsBar
        title={title}
        search={search}
        onSearchChange={setSearch}
        onDownloadTemplate={onDownloadTemplate}
        onAdd={() => setCreating(true)}
      />

      <PucTable
        cuentas={cuentas}
        isLoading={isLoading}
        search={search}
        onEdit={setEditing}
        onDelete={setPendingDelete}
      />

      {editing || creating ? (
        <PucFormModal
          initial={editing}
          onCancel={() => {
            setEditing(null);
            setCreating(false);
          }}
          onSubmit={(body) => saveMutation.mutate(body)}
          loading={saveMutation.isPending}
        />
      ) : null}

      <ConfirmDialog
        open={pendingDelete !== null}
        title="¿Eliminar cuenta PUC?"
        description={
          pendingDelete
            ? `Vas a eliminar la cuenta ${pendingDelete.codigo_puc} - ${pendingDelete.nombre_cuenta}.`
            : ""
        }
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        destructive
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (pendingDelete) deleteMutation.mutate(pendingDelete.id);
        }}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
};